/*
 * HomePage
 *
 * This is the first thing users see of our App, at the '/' route
 *
 */

import { FormattedMessage } from 'react-intl';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Spacings from '~/modules/ui-kit/spacings';
import Scrollable from '~/components/Scrollable';
import DrawerButton from '~/containers/DrawerButton';
import Dashboard from '~/components/Dashboard';
import LanguageButton from '~/components/LanguageButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { useMyDashboard } from '~/hooks/api/useMyDashboard';
import { useShiftsWithoutTeamLead } from '~/hooks/api/useShiftsWithoutTeamLead';
import { DashboardResource } from '~/generated';
import { InAppNotificationsButton } from '~/components/InAppNotificationsButton';
import Inline from '~/modules/ui-kit/spacings/inline';
import { useFetchCurrentEmergency as useFetchCurrentEmergency } from '~/hooks/api/useFetchCurrentEmergency';
import { useFetchPublisherEmergencyReportByPublisherId } from '~/hooks/api/useFetchPublisherEmergencyReportByPublisherId';
import { useProjectName } from '~/hooks/api/useProjectName';
import { getRouteApi } from '@tanstack/react-router';
import styles from './styles.module.css';
import messages from './messages';

const homeRouteApi = getRouteApi('/home');

const useFeatureToggle = () => {
  const { feature } = homeRouteApi.useSearch();
  return {
    isFeatureEnabled: (featureName: string) => feature?.includes(featureName),
  };
};

type DashboardProps = {
  publisherId: number;
  dashboardData: DashboardResource;
};

const TeamleaderDashboard = (props: DashboardProps) => {
  const { data: shiftsWithoutTeamLead, isLoading } = useShiftsWithoutTeamLead();
  return (
    <Dashboard
      publisherId={props.publisherId}
      dashboardData={props.dashboardData}
      missingShiftleadersData={
        isLoading || !shiftsWithoutTeamLead ? [] : shiftsWithoutTeamLead
      }
    />
  );
};

const PublisherDashboard = (props: DashboardProps) => (
  <Dashboard
    publisherId={props.publisherId}
    dashboardData={props.dashboardData}
    missingShiftleadersData={[]}
  />
);

export const HomePage = () => {
  const { isFeatureEnabled } = useFeatureToggle();
  const theme = useTheme();
  const { data: projectName, isLoading: isLoadingSettings } = useProjectName();
  const { data: publisher, isLoading: isLoadingPublisher } = usePublisherMe();
  const { data: currentEmergencyFetched } = useFetchCurrentEmergency();
  const { data: emergencyMode } = useFetchPublisherEmergencyReportByPublisherId(
    publisher?.id || 0,
    {
      enabled: !!publisher,
    }
  );
  const { data: dashboard, isLoading: isLoadingDashboard } = useMyDashboard();
  const isFetching =
    isLoadingPublisher || isLoadingDashboard || isLoadingSettings;
  return (
    <Scrollable
      className={styles['scrollable-content']}
      header={
        <AppBar position="static">
          <Toolbar>
            <DrawerButton />
            <Typography
              variant="h6"
              style={{ marginLeft: theme.spacing(2), flexGrow: 1 }}
            >
              <FormattedMessage {...messages.header} />
            </Typography>
            <Inline alignItems="center">
              <LanguageButton />
              {isFeatureEnabled('inAppNotifications') ? (
                <div>
                  <InAppNotificationsButton />
                </div>
              ) : null}
            </Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={isFetching}
    >
      {!isFetching && publisher && projectName && dashboard && (
        <div data-test="dashboard">
          <Spacings.Stack scale="s">
            <div className={styles['header-wrapper']}>
              <Spacings.Stack scale="none">
                <Typography variant="h6">
                  <FormattedMessage
                    {...messages.greeting}
                    values={{
                      firstName: publisher.firstName,
                      projectName: `${projectName}`,
                    }}
                  />
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  <FormattedMessage
                    {...messages.welcomeToTenant}
                    values={{
                      projectName: `${projectName}`,
                    }}
                  />
                </Typography>
              </Spacings.Stack>
            </div>
            {emergencyMode && currentEmergencyFetched?.emergency?.reason ? (
              <Alert severity="error">
                <AlertTitle>
                  <FormattedMessage {...messages.emergency} />:{' '}
                  {currentEmergencyFetched.emergency.reason}
                </AlertTitle>
                <p>
                  <FormattedMessage {...messages.pleaseReportYourStatus} />
                </p>
                <Button
                  variant="contained"
                  href="/emergency-status"
                  color="error"
                >
                  <FormattedMessage {...messages.reportStatus} />
                </Button>
              </Alert>
            ) : null}
          </Spacings.Stack>
          {(() => {
            if (isFetching) {
              return null;
            }
            if (publisher.isAdministrator || publisher.isShiftLeader) {
              return (
                <TeamleaderDashboard
                  publisherId={publisher.id}
                  dashboardData={dashboard}
                />
              );
            }
            return (
              <PublisherDashboard
                publisherId={publisher.id}
                dashboardData={dashboard}
              />
            );
          })()}
        </div>
      )}
    </Scrollable>
  );
};

export default HomePage;
